import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { unescapeHTML, titleize } from 'underscore.string';
import { withTranslation } from 'react-i18next';
import i18next from 'i18next';

import CargomaticInput from 'components/v2/input';
import LocationAutosuggest from 'components/site/location-autosuggest';
import SearchBox from 'components/site/SearchBox';

function getLocation(item) {
  if (item && item.name) {
    return item.name;
  }
  return i18next.t('UNKNOWN_LOCATION_NAME');
}

const createOnRemove = (item, onClick) => (e) => {
  e.stopPropagation();
  onClick(item);
};

const createRenderItem = (onClick) => (item) => (
  <a key={item._id} className="list-group-item" style={{ cursor: 'pointer' }}>
    <div className="flex justify-between">
      <p style={{ cursor: 'pointer' }} className="list-group-item-heading">
        <span className="searchName">{getLocation(item)}</span>
      </p>
      <button
        className="px-4 text-xl button"
        onClick={createOnRemove(item, onClick)}
      >
        <i className="text-xl fa fa-close text-grey hover:text-grey-darker" />
      </button>
    </div>
  </a>
);

class Location extends Component {
  constructor(props) {
    super(props);
    this.state = { locations: props.locations };
  }

  onComponentDidMount() {
    const location = this.props.location || {};
    const name = location.name || '';
    if (name) {
      this.search(name);
    }
  }

  clear() {
    this.setState({ locations: [] });
  }

  search = (nGram) => {
    const searchTerm = (nGram || '').toLowerCase();
    const filteredItems = (this.props.locations || []).filter((c) =>
      (c.name || '').toLowerCase().includes(searchTerm),
    );

    const locations = _.sortBy(filteredItems, (l) =>
      (l.name || '').toLowerCase(),
    );
    this.setState({ locations });
  };

  handleRemoveLocation = (location) => {
    this.props.onRemoveLocation(location);
  };

  handleAutocompleteNameChange = (item) =>
    this.props.onLocationFieldChange('name', item);

  handleNameChange = (value) => this.props.onLocationFieldChange('name', value);

  handleSuiteOrAptChange = (value) =>
    this.props.onLocationFieldChange('suite_or_apt', value);

  handleNameSelect = (value, item) => this.props.onNameSelect(item);

  handleAddressSelect = (item) => {
    this.setState({ suggestions: [{ item }] }, () => {
      this.props.onAddressSelect({ location: item });
    });
  };

  handleAddressChange = (value) => {
    if (value === '') {
      this.props.onAddressSelect({ location: { address: '' } });
    }
  };

  render() {
    const { location, locations, sequence, t } = this.props;
    const hasLocations = locations && locations.length > 0;

    return (
      <div className="grid grid-cols-12 gap-x-2 gap-y-3">
        <div className="col-span-full">
          {hasLocations && (
            <div className="relative">
              <SearchBox
                id={`${sequence}-name`}
                appendItems
                items={locations}
                clear={() => this.clear()}
                search={(nGram) => this.search(nGram)}
                placeholder={t('LOCATION_NAME')}
                onChange={this.handleAutocompleteNameChange}
                onSelect={this.handleNameSelect}
                value={unescapeHTML(location.name)}
                getItemValue={getLocation}
                renderItem={createRenderItem(this.handleRemoveLocation)}
                menuStyle={{ position: 'absolute', top: 40, left: 0 }}
              />
            </div>
          )}
          {!hasLocations && (
            <CargomaticInput
              type="text"
              hideLabel
              value={unescapeHTML(location.name)}
              placeholder={t('ENTER_LOCATION_NAME')}
              onChange={(value) => this.handleNameChange(value)}
              maxLength={80}
            />
          )}
        </div>
        <div className="col-span-7">
          <LocationAutosuggest
            className="form-field"
            name={unescapeHTML(location.name)}
            address={location.address}
            value={location.address}
            suggestions={[
              {
                name: location.name,
                address: location.address,
              },
            ]}
            callbackSelectedItem={this.handleAddressSelect}
            onChange={this.handleAddressChange}
            dropdownStyle={{ maxHeight: '500px', overflow: 'auto' }}
          />
        </div>
        <div className="col-span-5">
          <CargomaticInput
            type="text"
            value={location.suite_or_apt}
            hideLabel
            placeholder={t('SUITE_APT')}
            onChange={(value) => this.handleSuiteOrAptChange(value)}
            maxLength={80}
          />
        </div>
        <div className="col-span-5">
          <CargomaticInput
            type="text"
            value={location.contact ? location.contact.name : ''}
            hideLabel
            placeholder={titleize(t('CONTACT', { count: 1 }))}
            onChange={(value) => {
              this.props.onLocationContactChange('name', value);
            }}
            maxLength={40}
          />
        </div>
        <div className="col-span-7">
          <CargomaticInput
            type="phone"
            value={location.contact ? location.contact.phone : ''}
            hideLabel
            placeholder={titleize(t('PHONE', { count: 1 }))}
            onChange={(value) => {
              this.props.onLocationContactChange('phone', value);
            }}
            maxLength={25}
          />
        </div>
      </div>
    );
  }
}

Location.propTypes = {
  location: PropTypes.object.isRequired,
  locations: PropTypes.array.isRequired,
  onNameSelect: PropTypes.func.isRequired,
  onAddressSelect: PropTypes.func.isRequired,
  onLocationFieldChange: PropTypes.func.isRequired,
  onLocationContactChange: PropTypes.func.isRequired,
  onRemoveLocation: PropTypes.func.isRequired,
};

export default withTranslation()(Location);
